import { Injectable, inject } from '@angular/core';
import { isArrayOfEmptyStrings, isInvalidArray } from './array.methods';
import { FieldInputHtmlResponseMapperService } from './field-input-html-response-mapper.service';
import { HandleConditionalLogicService } from './handle-conditional-logic.service';
import { MultiSectionFormPreviewApiService } from './multi-section-form-preview-api.service';
import {
  IFormDataPayload,
  IOptioningData,
  IPreviewQuestionAnswerDetailsSaved,
  IQuesField,
} from './multi-section-form-preview.models';
import { MultiSectionFormPreviewService } from './multi-section-form-preview.service';

/**
 * SM-3610: Interface for conditional effects read from data attributes.
 * These are defined in the HTML on radio/checkbox options.
 */
interface IConditionalEffect {
  targetId: string;
  value?: string;
  disabled?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class FormResponseService {
  public msfps = inject(MultiSectionFormPreviewService);
  public msfapi = inject(MultiSectionFormPreviewApiService);
  public hcl = inject(HandleConditionalLogicService);
  public fihrm = inject(FieldInputHtmlResponseMapperService);

  constructor() {}

  getAlreadySavedAnswersIntoSupplierPreview() {
    if (
      this.msfps.mode === 'supplierEdit' ||
      this.msfps.mode === 'previewDisabled'
    ) {
      this.msfapi.getUserAnswers(this.msfps.surveyId).subscribe({
        next: (resp) => {
          if (resp?.Data) {
            const savedAnswersApiResp = [...resp.Data].map((ansObj) => ({
              id: ansObj.Key, // id of the Question
              answers: [...ansObj.Value], // array of answers (strings)
            }));
            savedAnswersApiResp.forEach((savedField: IFormDataPayload) =>
              this.mapSavedAnswersIntoSupplierPreviewUI(savedField)
            );

            // SM-3610: After loading all saved answers, enforce default answer rules
            this.enforceDefaultAnswerRules();
          }
          if (this.msfps.getFirstActiveSecAndSubSec().DisplayType === 'One') {
            this.msfps.showSectionFieldPreview(
              this.msfps.getFirstFieldFromActiveSecOrSubSec(),
            );
          } else {
            this.msfps.showEntireSectionPreview(
              this.msfps.getFirstActiveSecAndSubSec().UniqueID,
            );
          }
        },
        error: () => {},
      });
    }
  }

  /**
   * SM-3610: Public method to enforce all default answer rules based on data attributes.
   * Called on initial load and when source questions change.
   * Scans fields for data-set-target/data-set-value/data-set-disabled attributes and applies effects.
   */
  public enforceDefaultAnswerRules(): void {
    this.applyDataAttributeBasedRules();
  }

  /**
   * SM-3610: Apply rules based on data attributes defined in the field HTML.
   * Scans radio/dropdown fields for data-set-target, data-set-value, data-set-disabled attributes.
   */
  private applyDataAttributeBasedRules(): void {
    if (!this.msfps.allFields || this.msfps.allFields.length === 0) {
      return;
    }

    // Track which targets have been set by this run to handle clearing
    const appliedTargets = new Set<string>();

    // Scan all fields for data attributes
    this.msfps.allFields.forEach((field: IQuesField) => {
      if (!field.data || !field.data[1]) return;

      const fieldType = field.data[1].type?.toLowerCase();
      
      // Only process radio and dropdown fields
      if (!['radio', 'dropdown'].includes(fieldType)) return;

      // Get the HTML content to find data attributes
      const questionHtml = field.data[1]?.question?.changingThisBreaksApplicationSecurity;
      if (!questionHtml) return;

      // Parse the HTML to find inputs with data attributes
      const parser = new DOMParser();
      const doc = parser.parseFromString(questionHtml, 'text/html');

      // Get the selected answer from localStorage (most up-to-date) or nodeLogic
      const questionId = field.data[0]?.QuestionId?.replace('Radio_', '').replace('Dropdown_', '');
      const localStorageData = questionId ? localStorage.getItem(questionId) : null;
      let selectedOptionId: string | null = null;

      if (localStorageData) {
        try {
          const parsed = JSON.parse(localStorageData);
          selectedOptionId = parsed.answerUniqueId?.[0] ?? null;
        } catch (e) {
          // Fall back to nodeLogic
        }
      }

      const selectedAnswers = field.nodeLogic?.selectedAnswers;
      if (!selectedOptionId && selectedAnswers && selectedAnswers.length > 0) {
        const selectedAnswer = selectedAnswers[0];
        selectedOptionId = typeof selectedAnswer === 'object' ? selectedAnswer.optionid : null;
      }

      // Find all inputs (radio) or options (dropdown) with data attributes
      if (fieldType === 'radio') {
        const radioButtons = doc.querySelectorAll('input[type="radio"]');
        radioButtons.forEach((radio: Element) => {
          const radioInput = radio as HTMLInputElement;
          const effects = this.extractDataAttributes(radioInput);
          const isSelected = selectedOptionId === radioInput.value;

          effects.forEach(effect => {
            if (isSelected && field.answered) {
              // Apply the effect
              this.applyEffectToTarget(effect);
              appliedTargets.add(effect.targetId);
            }
          });
        });
      } else if (fieldType === 'dropdown') {
        const options = doc.querySelectorAll('option');
        options.forEach((option: Element) => {
          const optionEl = option as HTMLOptionElement;
          const effects = this.extractDataAttributesFromOption(optionEl);
          const isSelected = selectedOptionId === optionEl.value;

          effects.forEach(effect => {
            if (isSelected && field.answered) {
              // Apply the effect
              this.applyEffectToTarget(effect);
              appliedTargets.add(effect.targetId);
            }
          });
        });
      }
    });

    // Clear effects for targets that were not applied in this run
    // This handles the case when source answer changes and target should be unlocked
    this.clearUnusedEffects(appliedTargets);
  }

  /**
   * SM-3610: Extract data attributes from a radio input element.
   */
  private extractDataAttributes(element: Element): IConditionalEffect[] {
    const effects: IConditionalEffect[] = [];

    // Primary target
    const setTarget = element.getAttribute('data-set-target');
    if (setTarget) {
      effects.push({
        targetId: setTarget,
        value: element.getAttribute('data-set-value') ?? undefined,
        disabled: element.getAttribute('data-set-disabled') === 'true'
      });
    }

    // Numbered targets (data-set-target1, data-set-target2, etc.)
    let index = 1;
    let numberedTarget = element.getAttribute(`data-set-target${index}`);
    while (numberedTarget) {
      effects.push({
        targetId: numberedTarget,
        value: element.getAttribute(`data-set-value${index}`) ?? undefined,
        disabled: element.getAttribute(`data-set-disabled${index}`) === 'true'
      });
      index++;
      numberedTarget = element.getAttribute(`data-set-target${index}`);
    }

    return effects;
  }

  /**
   * SM-3610: Extract data attributes from a dropdown option element.
   */
  private extractDataAttributesFromOption(option: HTMLOptionElement): IConditionalEffect[] {
    const effects: IConditionalEffect[] = [];

    // Primary target
    const setTarget = option.getAttribute('data-set-target');
    if (setTarget) {
      effects.push({
        targetId: setTarget,
        value: option.getAttribute('data-set-value') ?? undefined,
        disabled: option.getAttribute('data-set-disabled') === 'true'
      });
    }

    // Numbered targets
    let index = 1;
    let numberedTarget = option.getAttribute(`data-set-target${index}`);
    while (numberedTarget) {
      effects.push({
        targetId: numberedTarget,
        value: option.getAttribute(`data-set-value${index}`) ?? undefined,
        disabled: option.getAttribute(`data-set-disabled${index}`) === 'true'
      });
      index++;
      numberedTarget = option.getAttribute(`data-set-target${index}`);
    }

    return effects;
  }

  /**
   * SM-3610: Apply an effect to a target field.
   */
  private applyEffectToTarget(effect: IConditionalEffect): void {
    // Find the target field by its element ID
    const questionId = effect.targetId.replace('Dropdown_', '').replace('Radio_', '').replace('Checkbox_', '');
    const targetFieldIndex = this.msfps.allFields?.findIndex(
      (f) => f.data[0]?.QuestionId === questionId
    ) ?? -1;

    if (targetFieldIndex === -1) return;

    const targetField = this.msfps.allFields[targetFieldIndex];
    if (!targetField) return;

    // Find the answer option that matches the value
    const fieldType = targetField.data[1]?.type?.toLowerCase();
    let answerText: string | undefined;

    if (fieldType === 'dropdown' && targetField.data[1]?.dropdowndata) {
      const option = targetField.data[1].dropdowndata.find(
        (opt: any) => opt.optionid === effect.value
      );
      answerText = option?.answer;
    } else if (fieldType === 'radio' && targetField.data[1]?.radiodata) {
      const option = targetField.data[1].radiodata.find(
        (opt: any) => opt.optionid === effect.value
      );
      answerText = option?.answer;
    }

    if (answerText) {
      // Use forceFieldToAnswer with the answer text
      this.forceFieldToAnswer(targetFieldIndex, answerText as any, effect.disabled ?? true);
    }
  }

  /**
   * SM-3610: Track which targets have active effects.
   */
  private activeEffectTargets = new Set<string>();

  /**
   * SM-3610: Clear effects for targets that are no longer active.
   */
  private clearUnusedEffects(currentActiveTargets: Set<string>): void {
    // Find targets that were active before but not now
    this.activeEffectTargets.forEach(targetId => {
      if (!currentActiveTargets.has(targetId)) {
        // Unlock this target
        const questionId = targetId.replace('Dropdown_', '').replace('Radio_', '').replace('Checkbox_', '');
        const targetFieldIndex = this.msfps.allFields?.findIndex(
          (f) => f.data[0]?.QuestionId === questionId
        ) ?? -1;

        if (targetFieldIndex !== -1) {
          this.unlockField(targetFieldIndex);
        }
      }
    });

    // Update the active targets set
    this.activeEffectTargets = new Set(currentActiveTargets);
  }

  /**
   * SM-3610: Unlock a field that was previously locked by a rule.
   * Re-enables the field UI so the user can make selections.
   */
  private unlockField(fieldIndex: number): void {
    const field = this.msfps.allFields[fieldIndex];
    if (!field) {
      return;
    }

    // Get current HTML and remove disabled attributes
    let currentHtml = field.data[1]?.question?.changingThisBreaksApplicationSecurity;
    if (!currentHtml) {
      return;
    }

    const parser = new DOMParser();
    const doc = parser.parseFromString(currentHtml, 'text/html');
    
    // Enable radio buttons
    doc.querySelectorAll('input[type="radio"]').forEach((input: Element) => {
      (input as HTMLInputElement).removeAttribute('disabled');
      (input as HTMLInputElement).style.backgroundColor = '';
    });
    
    // Enable dropdowns/selects
    doc.querySelectorAll('select').forEach((select: Element) => {
      (select as HTMLSelectElement).removeAttribute('disabled');
      (select as HTMLSelectElement).style.backgroundColor = '';
    });
    
    // Enable checkboxes
    doc.querySelectorAll('input[type="checkbox"]').forEach((input: Element) => {
      (input as HTMLInputElement).removeAttribute('disabled');
    });

    const serializer = new XMLSerializer();
    let updatedHtml = serializer.serializeToString(doc.body);
    // Clean up the serialized HTML (remove extra body tags if present)
    updatedHtml = updatedHtml.replace(/^<body[^>]*>/, '').replace(/<\/body>$/, '');
    
    this.msfps.allFields[fieldIndex].data[1].question.changingThisBreaksApplicationSecurity = updatedHtml;
  }

  /**
   * SM-3610: Generic method to force a field to a specific answer and optionally lock it.
   * @param fieldIndex - Index of the field in allFields array
   * @param answer - The answer to set (matches option's answer text)
   * @param lockField - Whether to disable the field after setting the answer (default: true)
   */
  private forceFieldToAnswer(fieldIndex: number, answer: string, lockField: boolean = true): void {
    const field = this.msfps.allFields[fieldIndex];
    if (!field) {
      return;
    }

    const fieldType = field?.data[1]?.type?.toLowerCase();
    let targetOption: any = null;
    let optionDataArray: any[] = [];

    if (fieldType === 'radio' && field.data[1]?.radiodata) {
      optionDataArray = field.data[1].radiodata;
      targetOption = optionDataArray.find(
        (opt) => opt.answer?.toLowerCase() === answer.toLowerCase()
      );
    } else if (fieldType === 'dropdown' && field.data[1]?.dropdowndata) {
      optionDataArray = field.data[1].dropdowndata;
      targetOption = optionDataArray.find(
        (opt) => opt.answer?.toLowerCase() === answer.toLowerCase()
      );
    }

    if (!targetOption) {
      return;
    }

    // Set target answer as selected, deselect others
    optionDataArray.forEach((opt) => {
      opt.selected = opt.optionid === targetOption.optionid;
    });

    // Update field state directly in allFields array
    this.msfps.allFields[fieldIndex].answered = true;
    this.msfps.allFields[fieldIndex].valid = true;
    this.msfps.allFields[fieldIndex].isAnswerModified = true;
    this.msfps.allFields[fieldIndex].isQnFieldVisibleOnPreviewArea = true;
    this.msfps.allFields[fieldIndex].isQnFieldVisibleOnNavListItems = true;

    if (this.msfps.allFields[fieldIndex].nodeLogic) {
      this.msfps.allFields[fieldIndex].nodeLogic.selectedAnswers = [targetOption];
    } else {
      this.msfps.allFields[fieldIndex].nodeLogic = { selectedAnswers: [targetOption] } as any;
    }

    // Update localStorage with the forced answer
    const questionId = field.data[0].QuestionId.replace('Radio_', '').replace('Dropdown_', '');
    const fieldAnswer: IPreviewQuestionAnswerDetailsSaved = {
      modifiedField: null,
      answerUniqueId: [targetOption.optionid],
      answerValue: [targetOption.answer],
      isAnswerValid: true,
      isAnswerModified: true,
    };
    localStorage.setItem(questionId, JSON.stringify(fieldAnswer));

    // Update the HTML UI to reflect the selected option
    let updatedHtml = this.fihrm.mapResponseIntoFieldUI(this.msfps.allFields[fieldIndex], 'setAction');
    if (updatedHtml) {
      // Optionally LOCK (disable) the field
      if (lockField) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(updatedHtml, 'text/html');
        
        // Disable radio buttons
        doc.querySelectorAll('input[type="radio"]').forEach((input: Element) => {
          (input as HTMLInputElement).setAttribute('disabled', 'true');
          (input as HTMLInputElement).style.backgroundColor = '#e0e0e0';
        });
        
        // Disable dropdowns/selects
        doc.querySelectorAll('select').forEach((select: Element) => {
          (select as HTMLSelectElement).setAttribute('disabled', 'true');
          (select as HTMLSelectElement).style.backgroundColor = '#e0e0e0';
        });
        
        // Disable checkboxes
        doc.querySelectorAll('input[type="checkbox"]').forEach((input: Element) => {
          (input as HTMLInputElement).setAttribute('disabled', 'true');
        });

        const serializer = new XMLSerializer();
        updatedHtml = serializer.serializeToString(doc.body);
        // Clean up the serialized HTML (remove extra body tags if present)
        updatedHtml = updatedHtml.replace(/^<body[^>]*>/, '').replace(/<\/body>$/, '');
      }
      
      this.msfps.allFields[fieldIndex].data[1].question.changingThisBreaksApplicationSecurity = updatedHtml;
    }

    // Update the fieldsSegregatedIntoSections reference
    const updatedField = this.msfps.allFields[fieldIndex];
    if (this.msfps.fieldsSegregatedIntoSections?.[updatedField.sectionId]) {
      const fieldIdx = this.msfps.fieldsSegregatedIntoSections[updatedField.sectionId].fieldsList.findIndex(
        (f) => f.data[0].QuestionId === updatedField.data[0].QuestionId
      );
      if (fieldIdx !== -1) {
        this.msfps.fieldsSegregatedIntoSections[updatedField.sectionId].fieldsList[fieldIdx] = updatedField;
      }
    }

    // Trigger conditional logic for dependent questions
    this.hcl.toggleConditionalNodesFor(this.msfps.allFields[fieldIndex], 'clickAction');
  }

  mapSavedAnswersIntoSupplierPreviewUI(savedField: IFormDataPayload) {
    if (
      !isArrayOfEmptyStrings(savedField.answers) &&
      savedField.answers?.length
    ) {

      if(this.msfps.mode === 'previewDisabled'){
        this.msfps.allFields?.map((f) => {
          const parser = new DOMParser();
          const html = parser.parseFromString(
            f.data[1].question.changingThisBreaksApplicationSecurity,
            'text/html'
          );
          if (f.data[1].TypeValue === 'TextInput' || f.data[1].TypeValue === 'Date') {
            Array.from(html.getElementsByTagName('input')).forEach((eventTarget) => {
              eventTarget.setAttribute('disabled', 'true');
              eventTarget.disabled = true;
            });
          }
          if( f.data[1].TypeValue === 'TextArea'){
            Array.from(html.getElementsByTagName('textarea')).forEach((eventTarget) => {
              eventTarget.setAttribute('disabled', 'true');
              eventTarget.disabled = true;
            });
          }

          if(f.data[1].TypeValue === 'CheckBox' || f.data[1].TypeValue === 'RadioButton'){
            Array.from(html.getElementsByTagName('input'))?.forEach((eventTarget) => {
              eventTarget.setAttribute('disabled', 'true');
              eventTarget.disabled = true;
            })
          }

          if(f.data[1].TypeValue === 'DropDownListSelection'){
            Array.from(html.getElementsByTagName('select'))?.forEach((eventTarget) => {
              const optionsArr = Array.from(eventTarget.options as HTMLOptionsCollection);
              
              optionsArr.forEach((option: HTMLOptionElement) => {
          
                if (this.msfps.mode === 'previewDisabled') {
                  option.setAttribute('disabled', 'true');
                  option.disabled = true;
                }
              });
            })
          }

          const serializer = new XMLSerializer();
          const modifiedHtml = serializer.serializeToString(html);
        
          f.data[1].question.changingThisBreaksApplicationSecurity = modifiedHtml;
        })
     
    }
     

      const field = this.msfps.allFields?.find(
        (f) => f.data[0]?.QuestionId === savedField?.id
      );

      // Preview the field section
      if (this.msfps.getSectionByFieldId(savedField.id)?.ParentID) {
        this.msfps.activeSecInfo = this.msfps.allSections?.find(
          (sec) =>
            sec?.UniqueID ===
            this.msfps.getSectionByFieldId(savedField.id)?.ParentID
        );
        this.msfps.activeSecSubSectionsInfo =
          this.msfps.activeSecInfo?.Sections;
      } else {
        this.msfps.activeSecInfo = this.msfps.getSectionByFieldId(
          savedField.id
        );
      }
      const fieldDetails: IPreviewQuestionAnswerDetailsSaved = {
        modifiedField: this.fihrm.mapResponseIntoFieldUI(field, 'setAction'),
        answerValue: savedField.answers,
        answerUniqueId: this.getAnswerUniqueIdsFromField(
          field,
          savedField.answers
        ),
        isAnswerModified: true,
        isAnswerValid: Boolean(
          savedField.answers.length &&
            !isArrayOfEmptyStrings(savedField.answers)
        ),
      };

      this.mapResponsesIntoFieldObj(fieldDetails, field, 'setAction');
    }
  }

  // @ts-ignore
  getAnswerUniqueIdsFromField(field: IQuesField, answers: string[]): string[] {
    if (
      ['dropdown', 'multiselect-dropdown', 'radio', 'checkbox'].includes(
        field.data[1]?.type?.toLowerCase()
      )
    ) {
      return answers.map(
        (ans) => this.getAnswerObjMappedTo(ans, field)?.optionid
      );
    }
  }

  // @ts-ignore
  getAnswerMappedTo(id: string, field: IQuesField): IOptioningData {
    let result;
    if (
      ['dropdown', 'multiselect-dropdown'].includes(
        field.data[1]?.type?.toLowerCase()
      )
    ) {
      result = field.data[1]?.dropdowndata.find(
        (ddopt) => ddopt.optionid === id
      );
    } else if (field.data[1]?.type?.toLowerCase() === 'radio') {
      result = field.data[1]?.radiodata.find(
        (radopt) => radopt.optionid === id
      );
    } else if (field.data[1]?.type?.toLowerCase() === 'checkbox') {
      result = field.data[1]?.checkboxdata.find(
        (checkopt) => checkopt.optionid === id
      );
    }
    return result;
  }

  // @ts-ignore
  getAnswerObjMappedTo(ans: string, field: IQuesField): IOptioningData {
    let result;
    if (
      ['dropdown', 'multiselect-dropdown'].includes(
        field.data[1]?.type?.toLowerCase()
      )
    ) {
      result = field.data[1]?.dropdowndata.find(
        (ddopt) => ddopt.answer === ans
      );
    } else if (field.data[1]?.type?.toLowerCase() === 'radio') {
      result = field.data[1]?.radiodata.find((radopt) => radopt.answer === ans);
    } else if (field.data[1]?.type?.toLowerCase() === 'checkbox') {
      result = field.data[1]?.checkboxdata.find(
        (checkopt) => checkopt.answer === ans
      );
    }
    return result;
  }

  handleResponse(
    field: IQuesField,
    triggerSubjectAction: 'clickAction' | 'setAction' | null = null
  ) {
    if (field.data[1]?.type?.toLowerCase() === 'dropdown') {
      this.handleSingleSelectDropdownFieldResponse(field, triggerSubjectAction);
    }
    if (field.data[1]?.type?.toLowerCase() === 'radio') {
      this.handleRadioFieldResponse(field, triggerSubjectAction);
    }
    if (field.data[1]?.type?.toLowerCase() === 'multiselect-dropdown') {
      this.handleMultiSelectDropdownFieldResponse(field, triggerSubjectAction);
    }
    if (field.data[1]?.type?.toLowerCase() === 'date') {
      this.handleDateFieldResponse(field, triggerSubjectAction);
    }
    if (['textbox', 'file'].includes(field.data[1]?.type?.toLowerCase())) {
      this.handleInputBoxResponse(field, triggerSubjectAction);
    }
    if (field.data[1]?.type?.toLowerCase() === 'checkbox') {
      this.handleCheckboxFieldResponse(field, triggerSubjectAction);
    }
    if (field.data[1]?.type?.toLowerCase() === 'textarea') {
      this.handleTextAreaResponse(field, triggerSubjectAction);
    }

    // SM-3610: Re-evaluate default answer rules after field response is processed
    // This handles cases where changing source question (e.g., Q2) should lock/unlock target (e.g., Q5)
    if (triggerSubjectAction === 'clickAction') {
      this.enforceDefaultAnswerRules();
    }
  }

  handleSingleSelectDropdownFieldResponse(
    field: IQuesField,
    triggerSubjectAction: 'setAction' | 'clickAction' | null = null
  ) {
    const getFieldDetails: IPreviewQuestionAnswerDetailsSaved = JSON.parse(
      localStorage.getItem(field.data[0].QuestionId.replace('Dropdown_', ''))
    );

    this.mapResponsesIntoFieldObj(getFieldDetails, field, triggerSubjectAction);
  }
  handleMultiSelectDropdownFieldResponse(
    field: IQuesField,
    triggerSubjectAction: 'setAction' | 'clickAction' | null = null
  ) {
    const getFieldDetails: IPreviewQuestionAnswerDetailsSaved = JSON.parse(
      localStorage.getItem(field.data[0].QuestionId.replace('Dropdown_', ''))
    );

    this.mapResponsesIntoFieldObj(getFieldDetails, field, triggerSubjectAction);
  }

  handleRadioFieldResponse(
    field: IQuesField,
    triggerSubjectAction: 'setAction' | 'clickAction' | null = null
  ) {
    const getFieldDetails: IPreviewQuestionAnswerDetailsSaved = JSON.parse(
      localStorage.getItem(field.data[0].QuestionId.replace('Radio_', ''))
    );
    this.mapResponsesIntoFieldObj(getFieldDetails, field, triggerSubjectAction);
  }

  handleDateFieldResponse(
    field: IQuesField,
    triggerSubjectAction: 'setAction' | 'clickAction' | null = null
  ) {
    const getFieldDetails: IPreviewQuestionAnswerDetailsSaved = JSON.parse(
      localStorage.getItem(field.data[0].QuestionId)
    );
    this.mapResponsesIntoFieldObj(getFieldDetails, field, triggerSubjectAction);
  }

  handleCheckboxFieldResponse(
    field: IQuesField,
    triggerSubjectAction: 'setAction' | 'clickAction' | null = null
  ) {
    const getFieldDetails: IPreviewQuestionAnswerDetailsSaved = JSON.parse(
      localStorage.getItem(
        field.data[0].QuestionId.replace('Checkbox_WRAPPER_', '')
      )
    );
    this.mapResponsesIntoFieldObj(getFieldDetails, field, triggerSubjectAction);
  }

  handleInputBoxResponse(
    field: IQuesField,
    triggerSubjectAction: 'setAction' | 'clickAction' | null = null
  ) {
    const getFieldDetails: IPreviewQuestionAnswerDetailsSaved = JSON.parse(
      localStorage.getItem(field.data[0].QuestionId)
    );
    this.mapResponsesIntoFieldObj(getFieldDetails, field, triggerSubjectAction);
  }

  handleTextAreaResponse(
    field: IQuesField,
    triggerSubjectAction: 'setAction' | 'clickAction' | null = null
  ) {
    const getFieldDetails: IPreviewQuestionAnswerDetailsSaved = JSON.parse(
      localStorage.getItem(field.data[0].QuestionId.replace('textareaID_', ''))
    );
    this.mapResponsesIntoFieldObj(getFieldDetails, field, triggerSubjectAction);
  }

  mapResponsesIntoFieldObj(
    getFieldDetails: IPreviewQuestionAnswerDetailsSaved,
    field: IQuesField,
    triggerSubjectAction: 'setAction' | 'clickAction' | null = null
  ) {
    if (getFieldDetails) {
      // Update responses into JSON reference
      let answers;

      if (
        ['dropdown', 'checkbox', 'multiselect-dropdown', 'radio'].includes(
          field.data[1]?.type?.toLowerCase()
        )
      ) {
        const getAnswersById = getFieldDetails.answerUniqueId?.map(
          (id) => this.getAnswerMappedTo(id, field) ?? ''
        );
        const getAnswersByVal = getFieldDetails.answerValue?.map(
          (ans) => this.getAnswerObjMappedTo(ans, field) ?? ''
        );

        answers = isInvalidArray(getAnswersById.map((x) => x.toString()))
          ? getAnswersByVal
          : getAnswersById;
      } else {
        answers = getFieldDetails.answerValue ?? [];
      }

      if (field['nodeLogic']) {
        field['nodeLogic'] = {
          ...field['nodeLogic'],
          selectedAnswers: answers,
        };
      } else {
        field['nodeLogic'] = { selectedAnswers: answers };
      }
      // SM-4317: Fix empty array truthy check - Boolean([]) is always true in JS
      field['answered'] = Array.isArray(getFieldDetails?.['answerValue'])
        ? !isInvalidArray(getFieldDetails['answerValue'])
        : Boolean(getFieldDetails?.['answerValue']);
      field['valid'] = getFieldDetails?.['isAnswerValid'];
      field['isAnswerModified'] = getFieldDetails?.['isAnswerModified'];

      // Look up: SPECEFIC FIELD TYPE LOGIC
      // Dropdown - Single & multiselect
      if (
        ['dropdown', 'multiselect-dropdown'].includes(
          field.data[1]?.type?.toLowerCase()
        )
      ) {
        field.data[1].dropdowndata.forEach((ddopt, idx) => {
          if (getFieldDetails.answerUniqueId?.includes(ddopt.optionid)) {
            field.data[1].dropdowndata[idx].selected = true;
          } else if (
            (getFieldDetails.answerValue as string[])?.includes(ddopt.answer) ||
            (getFieldDetails.answerValue as IOptioningData[])?.includes(ddopt)
          ) {
            field.data[1].dropdowndata[idx].selected = true;
          } else {
            field.data[1].dropdowndata[idx].selected = false;
          }
        });
      }

      // Radio
      else if (field.data[1]?.type?.toLowerCase() === 'radio') {
        field.data[1].radiodata.forEach((radopt, idx) => {
          if (getFieldDetails.answerUniqueId?.includes(radopt.optionid)) {
            field.data[1].radiodata[idx].selected = true;
          } else if (
            (getFieldDetails.answerValue as string[])?.includes(
              radopt.answer
            ) ||
            (getFieldDetails.answerValue as IOptioningData[])?.includes(radopt)
          ) {
            field.data[1].radiodata[idx].selected = true;
          } else {
            field.data[1].radiodata[idx].selected = false;
          }
        });
      }

      // checkbox
      else if (field.data[1]?.type?.toLowerCase() === 'checkbox') {
        field.data[1].checkboxdata.forEach((checkopt, idx) => {
          if (getFieldDetails.answerUniqueId?.includes(checkopt.optionid)) {
            field.data[1].checkboxdata[idx].selected = true;
          } else if (
            (getFieldDetails.answerValue as string[])?.includes(
              checkopt.answer
            ) ||
            (getFieldDetails.answerValue as IOptioningData[])?.includes(
              checkopt
            )
          ) {
            field.data[1].checkboxdata[idx].selected = true;
          } else {
            field.data[1].checkboxdata[idx].selected = false;
          }
        });
      }

      // map the HTML with the input
      field.data[1].question.changingThisBreaksApplicationSecurity =
        this.fihrm.mapResponseIntoFieldUI(field, 'clickAction');

      const parser = new DOMParser();
      const html = parser.parseFromString(
        this.fihrm.mapResponseIntoFieldUI(field, 'clickAction'),
        'text/html'
      );
      Array.from(html.getElementsByTagName('input')).forEach((eventTarget) => {
        // SM-4207: Add null check for field.nodeLogic?.id to prevent errors
        if(field.nodeLogic?.id && eventTarget.id.toString().includes(field.nodeLogic.id) && field.data[1]?.type?.toLowerCase() != 'date' && field.data[1]?.type?.toLowerCase() != 'file') {  
          const answerField: IPreviewQuestionAnswerDetailsSaved = JSON.parse(
            localStorage.getItem(eventTarget.id.toString())
          );
          // SM-4207: Only override valid if localStorage has a valid answer record
          if (answerField?.isAnswerValid !== undefined) {
            field.valid = answerField.isAnswerValid;
          }
        }
      });

      // Update responses into JSON
      const fieldIdx = this.msfps.fieldsSegregatedIntoSections[
        field.sectionId
      ].fieldsList.findIndex(
        (f) => f.data[0].QuestionId === field.data[0].QuestionId
      );
      this.msfps.fieldsSegregatedIntoSections[field.sectionId].fieldsList[
        fieldIdx
      ] = field;

      // according to conditional logic toggle respective nodes if field is answered/modified
      if (field.answered || field.isAnswerModified) {
        this.hcl.toggleConditionalNodesFor(field, triggerSubjectAction);
      }
    }
  }

  // Retrieval  From local storage
  getFieldResponseExistingInLocalStorage(
    field: IQuesField
  ): IPreviewQuestionAnswerDetailsSaved {
    let uniquePrefix = this.getFieldlocalStorageUniquePrefix(field);
    return JSON.parse(
      localStorage.getItem(field.data[0].QuestionId.replace(uniquePrefix, ''))
    );
  }

  doesFieldResponseExistInLocalStorage(field: IQuesField): Boolean {
    return Boolean(this.getFieldResponseExistingInLocalStorage(field));
  }

  getFieldlocalStorageUniquePrefix(field: IQuesField): string {
    const prefixes = {
      dropdown: 'Dropdown_',
      radio: 'Radio_',
      'multiselect-dropdown': '',
      date: '',
      textbox: '',
      checkbox: '',
      textarea: '',
    };
    return prefixes[field?.data[1]?.type?.toLowerCase()] ?? '';
  }

  clearAnswersForAllTheQuestions() {
    this.msfps.allFields.forEach((field) => {
      this.clearAnswerForTheQuestion(field);
    });
  }

  clearAnswerForTheQuestion(field: IQuesField) {
    if (
      !this.msfps.doesDisableInputExistFor(field) &&
      field.answered &&
      field.nodeLogic.selectedAnswers.length !== 0
    ) {
      field.answered = null;
      field.nodeLogic.selectedAnswers = null;
      field.data[1].question.changingThisBreaksApplicationSecurity =
        this.fihrm.clearResponseFromFieldUI(field);
    }
  }
}
